import { useState, useCallback, useRef, useEffect } from 'react'

const getLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getLogDateKey = (log) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(log?.dayKey || '')) {
    return log.dayKey
  }

  const matched = String(log?.timestamp || '').match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/)
  if (!matched) return null

  const [, year, month, day] = matched
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

const keepCurrentDayLogs = (logs, dayKey = getLocalDateKey()) => (
  (Array.isArray(logs) ? logs : []).flatMap((log) => {
    const logDayKey = getLogDateKey(log)
    if (logDayKey !== dayKey) return []
    return [{ ...log, dayKey: logDayKey }]
  })
)

const useLogManager = (options = {}) => {
  const {
    batchSize = 50,           // 批量处理大小
    persistKey = 'app_logs',  // 本地存储键名
    enablePersist = true      // 是否启用持久化
  } = options

  const [logs, setLogs] = useState([])
  const [isLoading] = useState(false)
  const logBuffer = useRef([])
  const flushTimer = useRef(null)
  const logIdCounter = useRef(0)

  // 从本地存储加载日志
  useEffect(() => {
    if (enablePersist) {
      try {
        const savedLogs = localStorage.getItem(persistKey)
        if (savedLogs) {
          const parsedLogs = JSON.parse(savedLogs)
          const currentDayLogs = keepCurrentDayLogs(parsedLogs)
          setLogs(currentDayLogs)
          localStorage.setItem(persistKey, JSON.stringify(currentDayLogs))
          logIdCounter.current = Math.max(
            ...parsedLogs.map(log => parseInt(String(log?.id || '').split('-')[0], 10) || 0),
            0
          ) + 1
        }
      } catch (error) {
        console.warn('Failed to load logs from localStorage:', error)
      }
    }
  }, [enablePersist, persistKey])

  // 保存日志到本地存储
  const persistLogs = useCallback((logsToSave) => {
    if (enablePersist) {
      try {
        localStorage.setItem(persistKey, JSON.stringify(keepCurrentDayLogs(logsToSave)))
      } catch (error) {
        console.warn('Failed to save logs to localStorage:', error)
      }
    }
  }, [enablePersist, persistKey])

  // 刷新缓冲区中的日志
  const flushLogs = useCallback(() => {
    if (logBuffer.current.length === 0) return

    const newLogs = keepCurrentDayLogs(logBuffer.current)
    logBuffer.current = []

    setLogs(prevLogs => {
      const currentDayLogs = keepCurrentDayLogs([...prevLogs, ...newLogs])
      
      // 持久化存储
      persistLogs(currentDayLogs)
      
      return currentDayLogs
    })

    if (flushTimer.current) {
      clearTimeout(flushTimer.current)
      flushTimer.current = null
    }
  }, [persistLogs])

  // 添加单条日志
  const addLog = useCallback((message, type = 'info', metadata = {}) => {
    const now = new Date()
    const timestamp = now.toLocaleString('zh-CN')
    const newLog = {
      id: `${logIdCounter.current++}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp,
      dayKey: getLocalDateKey(now),
      message: String(message),
      type,
      metadata
    }

    // 检查重复日志（基于消息内容和类型）
    const isDuplicate = logBuffer.current.some(log => 
      log.message === newLog.message && 
      log.type === newLog.type &&
      Math.abs(new Date(log.timestamp).getTime() - new Date(newLog.timestamp).getTime()) < 1000
    )

    if (!isDuplicate) {
      logBuffer.current.push(newLog)

      // 如果缓冲区达到批量大小，立即刷新
      if (logBuffer.current.length >= batchSize) {
        flushLogs()
      } else {
        // 否则设置延迟刷新
        if (flushTimer.current) {
          clearTimeout(flushTimer.current)
        }
        flushTimer.current = setTimeout(flushLogs, 100) // 100ms 延迟
      }
    }
  }, [batchSize, flushLogs])

  // 批量添加日志
  const addLogs = useCallback((logsArray) => {
    const now = new Date()
    const timestamp = now.toLocaleString('zh-CN')
    const dayKey = getLocalDateKey(now)
    const newLogs = logsArray.map((logData, index) => ({
      id: `${logIdCounter.current + index}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp,
      dayKey,
      message: String(logData.message || logData),
      type: logData.type || 'info',
      metadata: logData.metadata || {}
    }))

    logIdCounter.current += newLogs.length
    logBuffer.current.push(...newLogs)
    flushLogs()
  }, [flushLogs])

  // 清空所有日志
  const clearLogs = useCallback(() => {
    logBuffer.current = []
    if (flushTimer.current) {
      clearTimeout(flushTimer.current)
      flushTimer.current = null
    }
    setLogs([])
    if (enablePersist) {
      localStorage.removeItem(persistKey)
    }
  }, [enablePersist, persistKey])

  const replaceLogs = useCallback((nextLogs = []) => {
    logBuffer.current = []
    if (flushTimer.current) {
      clearTimeout(flushTimer.current)
      flushTimer.current = null
    }
    const normalizedLogs = keepCurrentDayLogs(nextLogs)
    setLogs(normalizedLogs)
    persistLogs(normalizedLogs)
  }, [persistLogs])

  // 删除指定日志
  const removeLog = useCallback((logId) => {
    setLogs(prevLogs => {
      const filteredLogs = prevLogs.filter(log => log.id !== logId)
      persistLogs(filteredLogs)
      return filteredLogs
    })
  }, [persistLogs])

  // 导出日志
  const exportLogs = useCallback((format = 'json') => {
    const allLogs = keepCurrentDayLogs([...logs, ...logBuffer.current])
    
    if (format === 'json') {
      return JSON.stringify(allLogs, null, 2)
    } else if (format === 'text') {
      return allLogs.map(log => 
        `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`
      ).join('\n')
    } else if (format === 'csv') {
      const headers = 'Timestamp,Type,Message,Metadata\n'
      const rows = allLogs.map(log => 
        `"${log.timestamp}","${log.type}","${log.message}","${JSON.stringify(log.metadata)}"`
      ).join('\n')
      return headers + rows
    }
    
    return allLogs
  }, [logs])

  // 获取日志统计信息
  const getLogStats = useCallback(() => {
    const allLogs = keepCurrentDayLogs([...logs, ...logBuffer.current])
    const stats = {
      total: allLogs.length,
      byType: {},
      recent: allLogs.slice(-10),
      oldestTimestamp: allLogs.length > 0 ? allLogs[0].timestamp : null,
      newestTimestamp: allLogs.length > 0 ? allLogs[allLogs.length - 1].timestamp : null
    }

    allLogs.forEach(log => {
      stats.byType[log.type] = (stats.byType[log.type] || 0) + 1
    })

    return stats
  }, [logs])

  // 跨越本地午夜时清掉前一天的日志；即使当天没有新日志也会执行。
  useEffect(() => {
    let midnightTimer = null

    const scheduleMidnightCleanup = () => {
      const now = new Date()
      const nextMidnight = new Date(now)
      nextMidnight.setHours(24, 0, 0, 0)
      const delay = Math.max(1, nextMidnight.getTime() - now.getTime())

      midnightTimer = setTimeout(() => {
        const currentDayKey = getLocalDateKey()
        logBuffer.current = keepCurrentDayLogs(logBuffer.current, currentDayKey)
        setLogs(prevLogs => {
          const currentDayLogs = keepCurrentDayLogs(prevLogs, currentDayKey)
          persistLogs(currentDayLogs)
          return currentDayLogs
        })
        scheduleMidnightCleanup()
      }, delay)
    }

    scheduleMidnightCleanup()
    return () => {
      if (midnightTimer) clearTimeout(midnightTimer)
    }
  }, [persistLogs])

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (flushTimer.current) {
        clearTimeout(flushTimer.current)
      }
      // 确保缓冲区中的日志被保存
      if (logBuffer.current.length > 0) {
        flushLogs()
      }
    }
  }, [flushLogs])

  return {
    logs,
    isLoading,
    addLog,
    addLogs,
    clearLogs,
    replaceLogs,
    removeLog,
    exportLogs,
    getLogStats,
    flushLogs
  }
}

export default useLogManager
